import { Column, Line } from '@ant-design/charts'
import { Alert } from 'antd'
import { useMemo } from 'react'

import { useChartTheme } from '@/components/charts/chartTheme'

import type { VmReadinessPoint } from '../hooks/useVmReadiness'
import type { VmActivityPoint } from '../hooks/vmActivity'

export function VmBarChart({ data }: { data: VmActivityPoint[] }) {
  const chart = useChartTheme()
  const terminatedMissing = data.some((d) => d.terminated === null)
  const barData = useMemo(
    () =>
      data.flatMap((d) => [
        { date: d.date, count: d.created, type: 'Created' },
        ...(d.terminated === null
          ? []
          : [{ date: d.date, count: d.terminated, type: 'Terminated' }]),
      ]),
    [data],
  )
  return (
    <>
      {terminatedMissing && (
        <Alert
          type="warning"
          showIcon
          message="Terminated VMs unavailable: the VM list failed to load"
          style={{ marginBottom: 8 }}
        />
      )}
      <Column
        data={barData}
        xField="date"
        yField="count"
        colorField="type"
        group
        height={250}
        axis={chart.axis}
        legend={chart.legend}
        style={{ maxWidth: 6 }}
        scale={{
          color: {
            domain: ['Created', 'Terminated'],
            range: ['#52c41a', '#ff4d4f'],
          },
        }}
      />
    </>
  )
}

export function VmReadinessChart({ data }: { data: VmReadinessPoint[] }) {
  const chart = useChartTheme()
  const chartData = useMemo(
    () =>
      data.map((d) => ({
        date: d.date,
        minutes:
          d.avgReadySeconds !== null
            ? Math.round((d.avgReadySeconds / 60) * 10) / 10
            : null,
      })),
    [data],
  )
  return (
    <Line
      data={chartData}
      xField="date"
      yField="minutes"
      height={250}
      axis={{
        x: { ...chart.axis.x, labelAutoHide: true },
        y: {
          ...chart.axis.y,
          labelFormatter: (v: number) => `${v}m`,
        },
      }}
      tooltip={{ items: [{ channel: 'y', valueFormatter: (v) => `${v}m` }] }}
      legend={false}
      style={{ lineWidth: 2 }}
      color="#722ed1"
      point={{ size: 3, shape: 'circle' }}
    />
  )
}
